/**
 * useBoardSidebarResize - thin wrapper around shared useSidebarResize.
 *
 * Board-specific constants + oversteer enabled.
 * Writes sidebar state into boardLayoutStore.
 */

import { useCallback } from 'react';
import { useSidebarResize, loadSidebarInitialWidth, loadSidebarInitialCollapsed } from '../../lib/useSidebarResize';
import type { BoardLayoutStore } from '../components/boardLayoutStore';

const SIDEBAR_STORAGE_KEY = 'roadmap_2026_sidebar_width';
const SIDEBAR_COLLAPSED_KEY = 'roadmap_2026_sidebar_collapsed';
const SIDEBAR_MIN = 80;
const SIDEBAR_MAX = 400;
const SIDEBAR_DEFAULT = 140;
const SIDEBAR_COLLAPSED_WIDTH = 24;
const SIDEBAR_COLLAPSE_THRESHOLD = 50;

export { SIDEBAR_DEFAULT, SIDEBAR_COLLAPSED_WIDTH };

const LOAD_CONFIG = { storageKeyWidth: SIDEBAR_STORAGE_KEY, min: SIDEBAR_MIN, max: SIDEBAR_MAX, defaultWidth: SIDEBAR_DEFAULT };

export function getInitialSidebarState() {
  const collapsed = loadSidebarInitialCollapsed(SIDEBAR_COLLAPSED_KEY);
  const expandedWidth = loadSidebarInitialWidth(LOAD_CONFIG);
  return {
    sidebarWidth: collapsed ? SIDEBAR_COLLAPSED_WIDTH : expandedWidth,
    isCollapsed: collapsed,
    isSidebarResizing: false,
  };
}

export function useBoardSidebarResize(store: BoardLayoutStore) {
  const onSidebarUpdate = useCallback((patch: { sidebarWidth?: number; isCollapsed?: boolean; isSidebarResizing?: boolean }) => {
    store.send({ type: 'sidebar.update', ...patch });
  }, [store]);

  return useSidebarResize({
    storageKeyWidth: SIDEBAR_STORAGE_KEY,
    storageKeyCollapsed: SIDEBAR_COLLAPSED_KEY,
    min: SIDEBAR_MIN,
    max: SIDEBAR_MAX,
    defaultWidth: SIDEBAR_DEFAULT,
    collapsedWidth: SIDEBAR_COLLAPSED_WIDTH,
    collapseThreshold: SIDEBAR_COLLAPSE_THRESHOLD,
    oversteer: true,
    onSidebarUpdate,
  });
}
